<template>
    <div class="creatives" v-loading.fullscreen.lock="fullscreenLoading">
        <div class="page-header">
            <h1 class="page-title">{{ $t("messages.creatives") }}
                <span v-if="campaign">
                    · {{ $t("messages.campaigns").toLowerCase() }}
                    <span v-if="user.role_id === 4">{{ campaign.campaign }}</span>
                    <a v-else :href="'/campaigns/' + campaign.id + '/edit'"
                        v-tippy="{ content: $t('messages.campaign_settings'), theme: 'light' }">
                        {{ campaign.campaign }}
                    </a>
                </span>
            </h1>
            <button v-if="campaign" class="create-button" @click="attachCreatives"
                :disabled="attachedCreativeIds.length === 0">
                <img src="/images/check.svg" alt="save" />
                {{ $t("messages.save") }}
            </button>
            <button v-else class="create-button" @click="goToCreatePage">
                <img src="/images/import.svg" alt="create" />
                {{ $t("messages.import") }}
            </button>
        </div>

        <div class="content">
            <div class="filters">
                <div class="filter-tab" :class="{ active: activeFilter === 'active' }"
                    @click="setActiveFilter('active')">
                    {{ $t("messages.active_creatives") }}
                    <span class="filter-count">{{ getCreativesCount('active') }}</span>
                </div>
                <div class="filter-tab" :class="{ active: activeFilter === 'paused' }"
                    @click="setActiveFilter('paused')">
                    {{ $t("messages.paused_creatives") }}
                    <span class="filter-count">{{ getCreativesCount('paused') }}</span>
                </div>
                <div class="filter-tab" :class="{ active: activeFilter === 'pending' }"
                    @click="setActiveFilter('pending')">
                    {{ $t("messages.pending_creatives") }}
                    <span class="filter-count">{{ getCreativesCount('pending') }}</span>
                </div>
            </div>

            <table class="creatives-table table">
                <thead>
                    <tr>
                        <th v-if="campaign" class="actions"></th>
                        <th class="name" @click="toggleSort">
                            <div class="sortable">
                                {{ $t('messages.creatives') }}
                                <img src="/images/up-down.svg" alt="sort" class="sort-icon">
                            </div>
                        </th>
                        <th class="status">{{ $t('messages.status') }}</th>
                        <th class="link">{{ $t('messages.landing_page') }}</th>
                        <th class="tracking">{{ $t('messages.external_verifier') }}</th>
                        <th class="counter_impr">{{ $t('messages.impression_counter') }}</th>
                        <th class="counter_click">{{ $t('messages.click_counter') }}</th>
                        <th v-if="!campaign" class="actions"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in sortedCreatives" :key="row.id">
                        <td v-if="campaign" class="actions">
                            <input type="checkbox" :id="row.id" :value="row.id" v-model="attachedCreativeIds" />
                            <label class="checkbox-large" :for="row.id"></label>
                        </td>
                        <td class="creative-name name">
                            <div v-if="editCreativeId !== row.id" class="creative-display">
                                <div class="name-content">
                                    <span @click="previewCreative(row)" class="title clickable">
                                        {{ row.creative }}
                                    </span>
                                </div>

                                <div class="info">
                                    <span v-if="row.type === 'audio'" v-tippy="{ content: 'Audio', theme: 'light' }"
                                        class="icon" alt="audio"></span>
                                    <span v-else-if="row.type === 'video'"
                                        v-tippy="{ content: 'Video', theme: 'light' }" class="icon" alt="video"></span>
                                    <span v-else-if="row.extension === 'zip'"
                                        v-tippy="{ content: 'HTML5', theme: 'light' }" class="icon" alt="zip"></span>
                                    <span v-else v-tippy="{ content: 'Image', theme: 'light' }" class="icon"
                                        alt="image"></span>

                                    <span class="separator">·</span>
                                    <span class="creative-id">ID: {{ row.id }}</span>

                                    <span v-if="['image', 'archive'].includes(row.type)" class="size-info">
                                        <span class="separator">·</span>
                                        <span class="icon" alt="size"></span>
                                        {{ row.size }}
                                    </span>
                                </div>

                            </div>
                            <div v-else class="name-info">
                                <input v-model="row.creative" type="text" />
                            </div>
                        </td>
                        <td class="status">
                            <span class="status-badge" :class="getStatusClass(row.status)">
                                {{ getStatusText(row.status) }}
                            </span>
                        </td>
                        <td class="link">
                            <textarea v-model="row.link" :disabled="editCreativeId !== row.id" name="link"
                                placeholder="https://"></textarea>
                        </td>
                        <td class="tracking">
                            <select v-model="row.tracking" :disabled="editCreativeId !== row.id" name="tracking">
                                <option value="" selected></option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                            </select>
                        </td>
                        <td class="counter_impr">
                            <textarea v-model="row.counter_impr" :disabled="editCreativeId !== row.id"
                                name="counter_impr" placeholder=""></textarea>
                        </td>
                        <td class="counter_click">
                            <textarea v-model="row.counter_click" :disabled="editCreativeId !== row.id"
                                name="counter_click" placeholder=""></textarea>
                        </td>
                        <td v-if="!campaign" class="actions">
                            <div class="actions-content">
                                <label v-if="editCreativeId !== row.id" @click="startEdit(row.id)" class="edit-label"
                                    v-tippy="{ content: $t('messages.edit'), theme: 'light' }"></label>
                                <label v-if="editCreativeId !== row.id" @click="deleteCreative(row.id)"
                                    class="delete-label"
                                    v-tippy="{ content: $t('messages.delete'), theme: 'light' }"></label>
                                <label v-if="editCreativeId === row.id" @click="saveCreative(row)" class="save-label"
                                    v-tippy="{ content: $t('messages.save'), theme: 'light' }"></label>
                                <label v-if="editCreativeId === row.id" @click="cancelEdit()" class="cancel-label"
                                    v-tippy="{ content: $t('messages.cancel_changes'), theme: 'light' }"></label>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "Creatives",
    props: ["campaign", "creatives", "attached_creative_ids", "user"],
    data() {
        return {
            localCreatives: [],
            attachedCreativeIds: [],

            editCreativeId: null,
            originalCreativeData: null,

            fullscreenLoading: false,

            filters: {
                creative: {
                    value: "",
                    keys: ["creative", "id"],
                },
            },

            activeFilter: 'active',
            sortAsc: true,
        };
    },
    mounted() {
        this.localCreatives = [...this.creatives];

        if (this.campaign) {
            this.attachedCreativeIds = [...this.attached_creative_ids];
        }
    },
    computed: {
        filteredCreatives() {
            const statusMap = {
                active: 'active',
                paused: 'paused',
                pending: 'pending',
            };
            const target = statusMap[this.activeFilter];
            return this.localCreatives.filter(c => c.status === target);
        },
        sortedCreatives() {
            const list = [...this.filteredCreatives];
            const dir = this.sortAsc ? 1 : -1;
            return list.sort((a, b) => {
                const an = (a.creative || '').toString().toLowerCase();
                const bn = (b.creative || '').toString().toLowerCase();
                if (an < bn) return -1 * dir;
                if (an > bn) return 1 * dir;
                return 0;
            });
        }
    },
    methods: {
        validateCreative(creative) {
            const errors = [];

            if (!creative.creative?.trim()) {
                errors.push(this.$t('messages.fill_required_fields'));
            }

            if (creative.link && creative.link.trim() && !this.isValidUrl(creative.link)) {
                errors.push(this.$t('messages.invalid_url_format'));
            }

            return errors;
        },

        isValidUrl(string) {
            try {
                new URL(string);
                return true;
            } catch (_) {
                return false;
            }
        },

        toggleSort() {
            this.sortAsc = !this.sortAsc;
        },
        setActiveFilter(filter) {
            this.activeFilter = filter;
        },

        startEdit(creativeId) {
            const creative = this.localCreatives.find(c => c.id === creativeId);
            if (creative) {
                this.originalCreativeData = { ...creative };
                this.editCreativeId = creativeId;
            }
        },

        cancelEdit() {
            if (this.originalCreativeData) {
                const creative = this.localCreatives.find(c => c.id === this.editCreativeId);
                if (creative) {
                    const hasChanges = this.hasChanges(creative, this.originalCreativeData);

                    if (hasChanges) {
                        this.$confirm(this.$t('messages.confirm_cancel_changes'), this.$t('messages.warning'), {
                            confirmButtonText: this.$t('messages.cancel'),
                            cancelButtonText: this.$t('messages.save'),
                            type: 'warning'
                        }).then(() => {
                            Object.assign(creative, this.originalCreativeData);
                            this.editCreativeId = null;
                            this.originalCreativeData = null;
                        }).catch(() => {
                        });
                    } else {
                        this.editCreativeId = null;
                        this.originalCreativeData = null;
                    }
                }
            }
        },

        hasChanges(current, original) {
            return current.creative !== original.creative ||
                current.link !== original.link ||
                current.tracking !== original.tracking ||
                current.counter_impr !== original.counter_impr ||
                current.counter_click !== original.counter_click;
        },

        handleError(error, defaultMessage) {
            this.fullscreenLoading = false;

            let message = defaultMessage;

            if (error.response) {
                const status = error.response.status;
                const serverMessage = error.response.data?.message;

                switch (status) {
                    case 422:
                        message = serverMessage || this.$t('messages.validation_error');
                        break;
                    case 404:
                        message = this.$t('messages.creative_not_found');
                        break;
                    case 401:
                        message = this.$t('messages.unauthorized_error');
                        break;
                    case 403:
                        message = this.$t('messages.forbidden_error');
                        break;
                    case 500:
                        message = this.$t('messages.server_error');
                        break;
                    default:
                        message = serverMessage || message;
                }
            } else if (error.request) {
                if (error.code === 'ECONNABORTED') {
                    message = this.$t('messages.timeout_error');
                } else {
                    message = this.$t('messages.network_error');
                }
            } else {
                message = error.message || message;
            }

            console.error('API Error:', {
                error: error,
                status: error.response?.status,
                message: message,
                timestamp: new Date().toISOString()
            });

            this.$notify({
                title: this.$t('messages.error_occurred'),
                message,
                type: 'error'
            });
        },

        isRetryableError(error) {
            if (error.response) {
                return error.response.status >= 500;
            } else if (error.request) {
                return true;
            }
            return false;
        },

        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        attachCreatives() {
            if (this.attachedCreativeIds.length === 0) {
                this.$notify({
                    title: this.$t('messages.error_occurred'),
                    message: this.$t('messages.select_at_least_one_creative'),
                    type: 'error'
                });
                return;
            }

            this.fullscreenLoading = true;

            axios
                .post("/creatives/attach", {
                    campaign_id: this.campaign.id,
                    creatives: [...this.attachedCreativeIds],
                })
                .then(() => {
                    return axios.post(`/creatives/${this.campaign.id}/export`, {
                        campaign_id: this.campaign.id,
                        creatives: [...this.attachedCreativeIds],
                    });
                })
                .then(() => {
                    this.fullscreenLoading = false;
                    this.$notify({ title: this.$t('messages.completed_successfully'), message: this.$t('messages.creatives_attached_successfully'), type: 'success' });
                })
                .catch((error) => {
                    this.handleError(error, this.$t('messages.failed_to_attach_creatives'));
                });
        },
        async saveCreative(item) {
            const validationErrors = this.validateCreative(item);
            if (validationErrors.length > 0) {
                this.$notify({
                    title: this.$t('messages.error_occurred'),
                    message: validationErrors.join(', '),
                    type: 'error'
                });
                return;
            }

            await this.saveCreativeWithRetry(item);
        },

        async saveCreativeWithRetry(item, maxRetries = 2) {
            this.fullscreenLoading = true;

            for (let attempt = 1; attempt <= maxRetries; attempt++) {
                try {
                    await axios.post("/creatives/edit", {
                        id: this.editCreativeId,
                        name: item.creative,
                        link: item.link,
                        tracking: item.tracking,
                        counter_impr: item.counter_impr,
                        counter_click: item.counter_click,
                    });

                    this.fullscreenLoading = false;
                    this.$notify({
                        title: this.$t('messages.completed_successfully'),
                        message: this.$t('messages.creative_saved_successfully'),
                        type: 'success'
                    });
                    this.editCreativeId = null;
                    this.originalCreativeData = null;
                    return;

                } catch (error) {
                    if (attempt === maxRetries) {
                        this.handleError(error, this.$t('messages.failed_to_save_creative'));
                    } else if (this.isRetryableError(error)) {
                        await this.delay(1000 * attempt);
                    } else {
                        this.handleError(error, this.$t('messages.failed_to_save_creative'));
                        break;
                    }
                }
            }
        },
        deleteCreative(id) {
            this.$confirm(this.$t('messages.confirm_delete_creative'), this.$t('messages.warning'), {
                confirmButtonText: this.$t('messages.delete'),
                cancelButtonText: this.$t('messages.cancel'),
                type: 'warning',
                confirmButtonClass: 'el-button--danger'
            }).then(() => {
                this.performDelete(id);
            }).catch(() => {
            });
        },

        performDelete(id) {
            this.fullscreenLoading = true;

            axios
                .post("/creatives/delete", { id })
                .then(() => {
                    this.localCreatives = this.localCreatives.filter(
                        (creative) => creative.id !== id
                    );

                    this.fullscreenLoading = false;

                    this.$notify({ title: this.$t('messages.completed_successfully'), message: this.$t('messages.creative_deleted_successfully'), type: 'success' });
                })
                .catch((error) => {
                    this.handleError(error, this.$t('messages.failed_to_delete_creative'));
                });
        },
        getCreativesCount(filter) {
            const statusMap = { active: 'active', paused: 'paused', pending: 'pending' };
            const target = statusMap[filter];
            return this.localCreatives.filter(c => c.status === target).length;
        },
        getStatusClass(status) {
            switch (status) {
                case 'active':
                    return 'status-active';
                case 'pending':
                    return 'status-pending';
                case 'completed':
                    return 'status-completed';
                case 'paused':
                    return 'status-paused';
                default:
                    return 'status-default';
            }
        },
        getStatusText(status) {
            switch (status) {
                case 'active':
                    return this.$t("messages.active_status");
                case 'paused':
                    return this.$t("messages.paused_status");
                case 'pending':
                    return this.$t("messages.pending_status");
                case 'completed':
                    return this.$t("messages.completed_status");
                default:
                    return status;
            }
        },
        goToCreatePage() {
            window.location.href = '/creatives/create';
        },
        previewCreative(creative) {
            window.open(`/creatives/preview/${creative.id}`, '_blank');
        }
    },
};
</script>

<style lang="scss" scoped>
label.checkbox-large::before {
    content: url("/images/checkbox-large.svg");
    height: 20px;
    margin: 0;
}

label.checkbox-large:hover::before {
    content: url("/images/checkbox-large.svg");
    filter: invert(80%) sepia(58%) saturate(2515%) hue-rotate(204deg) brightness(85%) contrast(94%);
    height: 20px;
    margin: 0;
}

input[type="checkbox"]:checked+label.checkbox-large::before {
    content: url("/images/checkbox-checked-large.svg");
    filter: none;
    height: 20px;
    margin: 0;
}
</style>
